'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/src/hooks/useAuth';

// Zod schema for registration form validation
const registerFormSchema = z.object({
  email: z.email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type RegisterFormData = z.infer<typeof registerFormSchema>;

export default function RegisterPage() {
  const { register: registerUser } = useAuth();
  const t = useTranslations('auth.login');
  const tCommon = useTranslations('common');
  const tRegistration = useTranslations('auth.registration');

  // Initialize form with react-hook-form + Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  // Form submission handler
  async function onSubmit(data: RegisterFormData) {
    try {
      await registerUser.mutateAsync({
        email: data.email,
        password: data.password,
      });
    } catch (error) {
      // Error handling is now managed by React Query
      console.error('Registration error:', error);
    }
  }

  // Calculate loading state
  const isLoading = isSubmitting || registerUser.isPending;

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <Card className="w-full max-w-sm space-y-4 p-6">
        <h1 className="text-xl font-semibold">{t('createAccount')}</h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <Label htmlFor="email">{t('email')}</Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-destructive text-sm">{errors.email.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="password">{t('password')}</Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              disabled={isLoading}
            />
            {errors.password && (
              <p className="text-destructive text-sm">
                {errors.password.message}
              </p>
            )}
          </div>
          {registerUser.error instanceof Error && (
            <p className="text-sm text-red-600">{registerUser.error.message}</p>
          )}
          <Button type="submit" className="w-full" disabled={isLoading}>
            {registerUser.isPending
              ? tCommon('creatingAccount')
              : t('createAccount')}
          </Button>
        </form>
        <p className="text-center text-sm">
          {tRegistration('alreadyHaveAccount')}{' '}
          <Link className="underline" href="/en/login">
            {tCommon('signIn')}
          </Link>
        </p>
        <p className="text-center text-xs text-gray-500">
          {t('afterRegistration')}
        </p>
      </Card>
    </div>
  );
}
